import autoScroll, {escapeWhenUpPlugin} from '@yrobot/auto-scroll';
import {handleStartGame, handleFinishGame, handleShareClick, handleCopyClick} from "./events.js"
// ==========================================
// 1. GLOBAL STATE & PAYLOAD LOADERS
// ==========================================
const LAUNCH_DATE = "2026-08-07" // Match launch date in .env
let allWordsData = null;  // full dictionary from all-words endpoint
let dailyWordData = null; // today's target word, path, and date from #game-app
let GAME_STARTED = false
let path = [];         

let isGameOver = false;
let START_WORD = "";
let TARGET_WORD = "";

// Decodes payloads
function decodePayload(base64Str) {
  try {
    const jsonStr = atob(base64Str);
    return JSON.parse(jsonStr);
  } catch (err) {
    console.error("Failed to decode payload:", err);
    return null;
  }
}

function getTargetDate() {
  // 1. Check for URL override: http://localhost:8888/?date=2026-08-03
  // const urlParams = new URLSearchParams(window.location.search);
  // const overrideDate = urlParams.get("date");

  // const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  // if (overrideDate && dateRegex.test(overrideDate)) {
  //   console.warn(`[DEV] Overriding target date to: ${overrideDate}`);
  //   return overrideDate;
  // }

  // 2. Fall back to local system clock
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

async function loadDailyWord() {
  try {
    // 1. Get player's LOCAL date in YYYY-MM-DD format
    // const now = new Date();
    // const year = now.getFullYear();
    // const month = String(now.getMonth() + 1).padStart(2, "0");
    // const day = String(now.getDate()).padStart(2, "0");
    // const localDateStr = `${year}-${month}-${day}`;
    const localDateStr = getTargetDate();
    // 2. Fetch from Netlify Edge Function
    const res = await fetch(`/api/get-daily-word?date=${localDateStr}`);

    if (!res.ok) {
      console.error(`Edge function error: ${res.status} ${res.statusText}`);
      return null;
    }

    const data = await res.json();

    if (data && data.payload) {
      // Decode Base64 string payload
      const gameData = JSON.parse(atob(data.payload));
      dailyWordData = gameData; 
      return gameData;
    }

    return null;
  } catch (err) {
    console.error("Failed to fetch daily word:", err);
    return null;
  }
}

// Fetch and decode the full allWords dataset
async function loadAllWords() {
  try {
    const response = await fetch("/api/all-words.json");
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    
    const container = await response.json();
    allWordsData = decodePayload(container.payload);
    return allWordsData;
  } catch (err) {
    console.error("Failed to fetch allWords dataset:", err);
    return null;
  }
}

// ==========================================
// 2. GAME LOGIC & UTILITIES
// ==========================================
// function rainEmojis({
//   emojis = ["🐢",],
//   count = 100,
// } = {}) {
//   for (let i = 0; i < count; i++) {
//     const el = document.createElement("div");

//     el.textContent = emojis[Math.floor(Math.random() * emojis.length)];

//     el.className = `
//       fixed
//       -top-12
//       pointer-events-none
//       select-none
//       z-[9999]
//       animate-[emoji-fall_3s_linear_forwards]
//     `;

//     el.style.left = `${Math.random() * 100}vw`;
//     el.style.fontSize = `${20 + Math.random() * 28}px`;
//     el.style.animationDuration = `${2 + Math.random() * 2}s`;
//     el.style.animationDelay = `${Math.random() * 0.5}s`;

//     document.body.appendChild(el);

//     el.addEventListener("animationend", () => el.remove());
//   }
// }
function showShareButton() {
  const shareBtn = document.getElementById("shareBtn");
  if (!shareBtn) return;

  shareBtn.classList.remove("hidden");

  shareBtn.addEventListener("click", () => {
    const { header, subHeader, description, emojiGrid, footer } = generateShareText(path, true, START_WORD, TARGET_WORD);
    handleWinDialog(header, subHeader, description, emojiGrid, footer);
    handleShareClick("web")
  });
}

function handleSaveBestStreak() {
  const currentStreak = parseInt(localStorage.getItem("currentStreak") || "0", 10);
  const bestStreak = parseInt(localStorage.getItem("bestStreak") || "0", 10);

  if (currentStreak > bestStreak) {
    localStorage.setItem("bestStreak", currentStreak.toString());
  }
}

function handleSaveCurrentStreak() {
  const lastSolvedWord = localStorage.getItem("lastSolvedWord");
  const yesterdayWord = dailyWordData?.yesterday?.word?.toUpperCase();
  const currentStreak = parseInt(localStorage.getItem("currentStreak") || "0", 10);

  // 1. Guard: If today's word was ALREADY solved, don't increment or reset again today
  if (lastSolvedWord === START_WORD) {
    return;
  }

  // 2. Increment streak if they solved yesterday's word, otherwise start at 1
  let newStreak = 1;
  if (lastSolvedWord && yesterdayWord && lastSolvedWord === yesterdayWord) {
    newStreak = currentStreak + 1;
  }

  // 3. Save updated streak & last solved word
  localStorage.setItem("currentStreak", newStreak.toString());
  localStorage.setItem("lastSolvedWord", START_WORD);

  // 4. Update best streak
  handleSaveBestStreak();
}

function checkAndResetStreakOnLoad() {
  const lastSolvedWord = localStorage.getItem("lastSolvedWord");
  const todayWord = START_WORD; // Today's daily word
  const yesterdayWord = dailyWordData?.yesterday?.word?.toUpperCase();

  // If they haven't solved today's word AND haven't solved yesterday's word, reset streak
  if (lastSolvedWord && lastSolvedWord !== todayWord && lastSolvedWord !== yesterdayWord) {
    localStorage.setItem("currentStreak", "0");
  }
}

function getPathFromLocalStorage() {
  const pathString = localStorage.getItem("path");
  const savedStartWord = localStorage.getItem("currentStartWord");

  // Check if we have a saved path AND it belongs to TODAY's start word
  if (pathString && savedStartWord === START_WORD) {
    try {
      return JSON.parse(pathString);
    } catch (e) {
      console.error("Error parsing saved path:", e);
    }
  }

  // If new day / stale word, reset local storage for the active path
  localStorage.setItem("currentStartWord", START_WORD);
  localStorage.setItem("path", JSON.stringify([START_WORD]));
  return [START_WORD];
}
// Checks if a word is exactly 1 letter different from another
function isOneLetterOff(w1, w2) {
  if (w1.length !== w2.length) return false;
  let diffs = 0;
  for (let i = 0; i < w1.length; i++) {
    if (w1[i] !== w2[i]) diffs++;
  }
  return diffs === 1;
}

// Validates if guess exists in allWords
function isValidWord(word) {
  if (!allWordsData) return false;
  return Object.prototype.hasOwnProperty.call(allWordsData, word.toLowerCase());
}

function updateBoardGradient() {
  const boardEl = document.getElementById("board");
  const gradientEl = document.getElementById("board-gradient");
  if (!boardEl || !gradientEl) return;

  // Check if content overflows vertically AND user has scrolled down
  const hasOverflow = boardEl.scrollHeight > boardEl.clientHeight;
  const isScrolledDown = boardEl.scrollTop > 5;

  if (hasOverflow && isScrolledDown) {
    gradientEl.classList.remove("hidden");
  } else {
    gradientEl.classList.add("hidden");
  }
}

document.getElementById("board")?.addEventListener("scroll", updateBoardGradient);

function getPuzzleNumber(launchDateStr = LAUNCH_DATE) {
  // Parse launch date (YYYY-MM-DD) as local start of day
  const [lYear, lMonth, lDay] = launchDateStr.split("-").map(Number);
  const launchDate = new Date(lYear, lMonth - 1, lDay);

  // Get current local date (midnight boundary)
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysElapsed = Math.round((today - launchDate) / msPerDay);
  return Math.max(1, daysElapsed + 1);
}

// Win dialog
// Update localstorage, render content
function handleWinDialog(header, subHeader, description, emojiGrid, footer) {
  localStorage.setItem("todayWinHeader", JSON.stringify(header))
  localStorage.setItem("todayWinEmojiGrid", JSON.stringify(emojiGrid))
  const winDialog = document.getElementById("winDialog")
  const winHeader = winDialog.querySelector("#win-header")
  const winSubHeader = winDialog.querySelector("#win-sub-header")
  const winDescription = winDialog.querySelector("#win-description")
  const winGrid = winDialog.querySelector("#win-grid")
  const winFooter = winDialog.querySelector("#win-footer")
  winHeader.textContent = header
  winSubHeader.textContent = subHeader
  winDescription.textContent = description
  winGrid.textContent = emojiGrid
  winFooter.textContent = footer

  winDialog.showModal();

  const copyBtn = winDialog.querySelector("#copyWinBtn");
  copyBtn.addEventListener("click", async () => {
    const copytext = `${header}\n${emojiGrid}${footer}`;
    try {
      await navigator.clipboard.writeText(copytext);
      const icons = copyBtn.querySelectorAll(".icon");
      const copyBtnText = copyBtn.querySelector("span");

      copyBtnText.textContent = "Copied!";
      icons.forEach(icon => icon.classList.toggle("hidden"));
      handleCopyClick("web")
      setTimeout(() => {
        copyBtnText.textContent = "Copy";
        icons.forEach(icon => icon.classList.toggle("hidden"));
      }, 2000);

    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  });
}

// Generate the messages displayed when user wins
function generateShareText(path, isWin, startWord, targetWord = "TURD") {
  const puzzleNumber = getPuzzleNumber()
  const totalSteps = path.length - 1;
  const bestSteps = dailyWordData.path.length - 1
  const header = `Turdle #${puzzleNumber} ${totalSteps}/${bestSteps}`;
  const subHeader = `${startWord} → ${targetWord}`;
  const description = `You used ${totalSteps} guesses to get to TURD, the quickest solution was ${bestSteps} guesses.`

  const emojiGrid = path.map((word, rowIndex) => {
    const isStart = rowIndex === 0;

    return [...word].map((letter, letterIndex) => {
      // 1. Starter word gets its own distinct tile
      if (isStart) return "🟫"; 

      // 2. Letter matches TURD in exact position
      if (letter.toUpperCase() === targetWord[letterIndex]) {
        return "🐢"; 
      }

      // 3. Regular step letter
      return "🟨"; 
    }).join("");
  }).join("\n");

  const footer = "\n\nhttps://turdle.blog/";

  return { header, subHeader, description, emojiGrid, footer}
}

function bindVirtualKeyboard() {
  const inputEl = document.getElementById("guess-input");
  const formEl = document.getElementById("guess-form");
  if (!inputEl || !formEl) return;

  // Listen for clicks on any virtual key
  document.querySelectorAll(".key").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (isGameOver) return;

      const keyValue = btn.textContent.trim();
      const currentValue = inputEl.value;

      // Append letter if under the 4-character limit
      if (currentValue.length < 4 && /^[A-Z]$/.test(keyValue)) {
        inputEl.value = currentValue + keyValue;
        inputEl.dispatchEvent(new Event("input")); // Clears error messages on typing
      }
      
      inputEl.focus();
    });
  });

  document.querySelectorAll(".key-wide").forEach((btn) => {
  btn.addEventListener("click", () => {
    if (isGameOver) return;
    const keyValue = btn.textContent.trim()
    const currentValue = inputEl.value;
    if (keyValue === "⌫" || keyValue === "Back") {
        inputEl.value = currentValue.slice(0, -1);
        inputEl.dispatchEvent(new Event("input"));
        return;
      }
      if (keyValue === "↵" || keyValue === "Enter") {
        formEl.requestSubmit();
        return;
      }
  })
})
}

// Renders the word ladder board
function renderBoard() {
  const boardEl = document.getElementById("board");
  const inputEl = document.getElementById("guess-input");
  if (!boardEl) return;

  boardEl.innerHTML = "";
  
  const targetWord = "TURD";

  // 1. Render already played words
  path.forEach((word, rowIndex) => {
    const row = document.createElement("div");
    row.className = "word-row";
    const isStart = rowIndex === 0;

    [...word].forEach((letter, letterIndex) => {
      const box = document.createElement("div");
      
      const isExactMatch = 
        !isStart && 
        letter.toUpperCase() === targetWord[letterIndex];

      const classes = [
        "letter-box",
        isStart ? "start-node" : "",
        isExactMatch ? "turd-letter" : ""
      ].filter(Boolean).join(" ");

      box.className = classes;
      box.textContent = letter;
      row.appendChild(box);
    });
    
    boardEl.appendChild(row);
  });

  // 2. Render live pending guess row if game is active
  if (!isGameOver && inputEl) {
    const pendingWord = inputEl.value.toUpperCase().padEnd(4, " ");
    const pendingRow = document.createElement("div");
    pendingRow.className = "word-row pending-row";

    [...pendingWord].forEach((char, letterIndex) => {
      const box = document.createElement("div");
      const isFilled = char !== " ";

      const classes = [
        "letter-box",
        "pending-node",
        isFilled ? "has-letter" : "empty-letter"
      ].filter(Boolean).join(" ");

      box.className = classes;
      box.textContent = isFilled ? char : "";
      pendingRow.appendChild(box);
    });

    boardEl.appendChild(pendingRow);
  }

  autoScroll({
    selector: "#board",
    plugins: [escapeWhenUpPlugin()],
  });

  requestAnimationFrame(() => {
    updateBoardGradient();
  });
}

function clearPendingRow() {
  const pendingRowLetterBoxes = document.querySelectorAll(".pending-row .letter-box");
  pendingRowLetterBoxes.forEach((box) => {
    box.textContent = ""
  })
}

function bindFormEvents() {
  const formEl = document.getElementById("guess-form");
  const inputEl = document.getElementById("guess-input");
  const inputBtn = document.getElementById("input-btn");
  const messageEl = document.getElementById("message");

  // Guard against missing essential DOM elements
  if (!formEl || !inputEl || !messageEl) return;

  // Ensure input doesn't trigger soft keyboard / double input on touch devices
  inputEl.setAttribute("inputmode", "none");

  // --- Core Helpers ---

  // Trigger rumble animation on a target DOM element
  const triggerRumble = (el) => {
    if (!el) return;
    el.classList.add("rumble");
    setTimeout(() => el.classList.remove("rumble"), 300);
  };

  // Reset input value & fire input event to sync state & re-render board UI
  const clearInputAndBoard = () => {
    inputEl.value = "";
    inputEl.dispatchEvent(new Event("input"));
  };

  // Display validation message & schedule clean reset
  const triggerErrorRumble = (msg) => {
    messageEl.textContent = msg;
    const pendingRow = document.querySelector(".pending-row");
    triggerRumble(pendingRow);

    setTimeout(() => {
      messageEl.textContent = "";
      if (typeof clearPendingRow === "function") clearPendingRow();
      clearInputAndBoard();
    }, 900);
  };

  // --- Action Handlers ---

  const handleCharInput = (char, keyEl = null) => {
    if (isGameOver) return;

    // Locate matching key for rumble animation if not passed directly
    if (!keyEl) {
      const keys = document.querySelectorAll(".key:not(.key-back):not(.key-enter)");
      keys.forEach((k) => {
        if (k.textContent.trim().toLowerCase() === char.toLowerCase()) {
          keyEl = k;
        }
      });
    }
    triggerRumble(keyEl);

    if (inputEl.value.length < 4) {
      inputEl.value += char.toUpperCase();
      inputEl.dispatchEvent(new Event("input"));
    }
  };

  const handleBackspaceInput = (keyEl = null) => {
    if (isGameOver) return;

    const backKey = keyEl || document.querySelector(".key-back");
    triggerRumble(backKey);

    if (inputEl.value.length > 0) {
      inputEl.value = inputEl.value.slice(0, -1);
      inputEl.dispatchEvent(new Event("input"));
    }
  };

  const handleEnterInput = (keyEl = null) => {
    if (isGameOver) return;

    const enterKey = keyEl || document.querySelector(".key-enter");
    triggerRumble(enterKey);

    formEl.requestSubmit();
  };

  // --- Virtual Keyboard Binds (pointerdown prevents double-firing) ---

  const bindVirtualKeyboardEvents = () => {
    // 1. Letter keys
    const letterKeys = document.querySelectorAll(".key:not(.key-back):not(.key-enter)");
    letterKeys.forEach((key) => {
      key.addEventListener("pointerdown", (e) => {
        e.preventDefault(); // Prevents click/touchstart double-fire
        const char = key.textContent.trim();
        if (char && /^[a-zA-Z]$/.test(char)) {
          handleCharInput(char, key);
        }
      });
    });

    // 2. Backspace key
    const backKey = document.querySelector(".key-back");
    if (backKey) {
      backKey.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        handleBackspaceInput(backKey);
      });
    }

    // 3. Enter key
    const enterKey = document.querySelector(".key-enter");
    if (enterKey) {
      enterKey.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        handleEnterInput(enterKey);
      });
    }
  };

  // --- Event Listeners ---

  // Single 'input' event listener to clear messages & trigger board render
  inputEl.addEventListener("input", () => {
    if (messageEl) messageEl.textContent = "";
    if (typeof renderBoard === "function") renderBoard();
  });

  // Physical Keyboard Listener
  document.addEventListener("keydown", (e) => {
    if (isGameOver) return;

    // Ignore browser/system key combos (e.g., Cmd+R, Ctrl+Shift+I)
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.key.length === 1 && /^[a-zA-Z]$/.test(e.key)) {
      handleCharInput(e.key);
      return;
    }

    if (e.key === "Backspace") {
      handleBackspaceInput();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      handleEnterInput();
      return;
    }
  });

  // Attach virtual keyboard listeners
  bindVirtualKeyboardEvents();

  // Form Submit Handler
  formEl.addEventListener("submit", (e) => {
    e.preventDefault();
    messageEl.textContent = "";

    if (isGameOver) return;

    const guess = inputEl.value.trim().toUpperCase();
    const currentWord = path[path.length - 1];

    // Validation Checks
    if (guess.length !== 4) {
      triggerErrorRumble("Must be 4 letters!");
      return;
    }
    if (typeof isValidWord === "function" && !isValidWord(guess)) {
      triggerErrorRumble("Not in Turdy's list!");
      return;
    }
    if (typeof isOneLetterOff === "function" && !isOneLetterOff(currentWord, guess)) {
      triggerErrorRumble(`Must change exactly 1 letter from ${currentWord}!`);
      return;
    }

    if (!GAME_STARTED) {
      handleStartGame("standard");
      GAME_STARTED = true;
    }

    // Valid step
    path.push(guess);
    localStorage.setItem("path", JSON.stringify(path));
    localStorage.setItem("currentStartWord", START_WORD);
    clearInputAndBoard();

    // Win Condition
    if (guess === TARGET_WORD) {
      // if (typeof rainEmojis === "function") rainEmojis();
      isGameOver = true;
      inputEl.disabled = true;
      if (inputBtn) inputBtn.disabled = true;

      if (typeof generateShareText === "function" && typeof handleWinDialog === "function") {
        const { header, subHeader, description, emojiGrid, footer } = generateShareText(
          path,
          true,
          START_WORD,
          TARGET_WORD
        );
        handleWinDialog(header, subHeader, description, emojiGrid, footer);
      }

      const wins = parseInt(localStorage.getItem("totalWins") || "0", 10);
      localStorage.setItem("totalWins", (wins + 1).toString());
      const score = path.length - 1
      handleFinishGame(score)
      if (typeof showShareButton === "function") showShareButton();
      if (typeof handleSaveCurrentStreak === "function") handleSaveCurrentStreak();
    }

    if (typeof renderBoard === "function") renderBoard();
  });
}

// ==========================================
// 3. INITIALIZATION
// ==========================================
async function initGame() {
  const daily = await loadDailyWord();
  const dictionary = await loadAllWords();

  if (!daily || !dictionary) {
    console.error("Initialization failed: Missing game data.");
    return;
  }

  const yesterdayBtn = document.getElementById("yesterdayBtn");
  if (
    yesterdayBtn && 
    dailyWordData?.yesterday?.path && 
    dailyWordData?.yesterday?.word
  ) {
    yesterdayBtn.classList.remove("hidden");
  }

  // Set the START_WORD
  START_WORD = (daily.word || daily.path[0]).toUpperCase();
  TARGET_WORD = "TURD"; 
  
  // Load user game progress
  path = getPathFromLocalStorage();

  // Check if game was already won today
  const inputEl = document.getElementById("guess-input");
  const inputBtn = document.getElementById("input-btn");
  const lastSolved = localStorage.getItem("lastSolvedWord");
  const isPathWon = path.length > 0 && path[path.length - 1] === TARGET_WORD;
  const isAlreadySolvedToday = lastSolved === START_WORD;

  if (isPathWon || isAlreadySolvedToday) {
    isGameOver = true;
    if (inputEl) inputEl.disabled = true;
    // if (inputBtn) inputBtn.disabled = true;
    showShareButton();
  } else {
    isGameOver = false;
    if (inputEl) inputEl.disabled = false; // Ensure input is unlocked for new day!
  }

  checkAndResetStreakOnLoad()

  // Bind listeners & initial render
  bindFormEvents();
  renderBoard();
  setTimeout(() => {
    if (daily && dictionary) {
      document.querySelector("#board-wrapper #board-loading").classList.add("hidden");
      document.querySelector("#board-wrapper #board").classList.remove("hidden");
    }
  }, 600);
}

// Return content for the "View yesterday's" button
function formatYesterdayPath(path) {
  if (!path || !Array.isArray(path) || path.length === 0) {
    return { wordsHtml: "<p>No data available</p>", emojiGrid: "" };
  }
  
  const target = path[path.length - 1]; // e.g., "turd"
  const wordListItems = [];
  const emojiRows = [];

  path.forEach((word, index) => {
    const isStart = index === 0;
    let wordBoxesHtml = "";
    let emojiRow = "";

    for (let i = 0; i < word.length; i++) {
      const isExactMatch = word[i] === target[i];

      const classes = [
        "letter-box",
        isStart ? "start-node" : "",
        isExactMatch ? "turd-letter" : ""
      ].filter(Boolean).join(" ");

      wordBoxesHtml += `<span class="${classes}">${word[i].toUpperCase()}</span>`;
    }

    let label = `Step ${index}`;
    if (isStart) label = "START";
    if (index === path.length - 1) label = "TARGET";

    wordListItems.push(`
      <div class="word-row">${wordBoxesHtml}</div>
    `);
  });

  const wordsHtml = `${wordListItems.join("")}`;
  const emojiGrid = emojiRows.join("\n");

  return { wordsHtml, emojiGrid };
}

// Init game
document.addEventListener("DOMContentLoaded", initGame);

const yesterdayDialog = document.getElementById("yesterdayDialog");
const yesterdayCloseBtn = document.getElementById("yesterdayCloseBtn");
const yesterdayBtn = document.getElementById("yesterdayBtn");
if (yesterdayCloseBtn) {
  yesterdayCloseBtn.addEventListener("click", () => {
    yesterdayDialog.close()
  })
}

// document.addEventListener("DOMContentLoaded", () => {
//   if (yesterdayBtn && dailyWordData && dailyWordData["yesterday"] && dailyWordData["yesterday"]["path"] && dailyWordData["yesterday"]["word"]) {
//     yesterdayBtn.classList.remove("hidden")
//   }
// })

if (yesterdayBtn) {
  yesterdayBtn.addEventListener("click", () => {
    const path = dailyWordData["yesterday"]["path"] || [];
    const steps = path.length > 0 ? path.length - 1 : 0;
    const { wordsHtml, emojiGrid } = formatYesterdayPath(path);
    const yesterdayContent = document.getElementById("yesterdayContent");
    const yesterdayGrid = document.getElementById("yesterdayGrid");
    yesterdayContent.innerHTML = `
      <p class="mb-3">Yesterday's <strong>TURD</strong> was <strong>${dailyWordData["yesterday"]["word"].toUpperCase()}</strong> and the fewest guesses required was <strong>${steps}</strong>.</p>
      <p>Here's one of them!</p>
    `;

    yesterdayGrid.innerHTML = `
      <div class="flex flex-col gap-4">
        ${wordsHtml}
        <pre class="emoji-grid-display">${emojiGrid}</pre>
      </div>
    `;

    yesterdayDialog.showModal();
  });
}